import { Loader2, Zap } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useProjectSummary } from '@/features/project-dashboard/hooks/useProjectSummary';
import { QuickPunchConfigurationEditor } from '@/features/punch-lists/components/QuickPunchConfigurationEditor';
import {
  useQuickPunchProjectConfiguration,
  useUpdateQuickPunchProjectConfiguration,
} from '@/features/punch-lists/hooks';
import { BaseLayout } from '@/shared/components/layout';
import { getErrorMessage } from '@/shared/errors';
import { useToast } from '@/shared/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { VerticalTab } from '@/shared/ui/custom/VerticalTab';

const PROJECT_SETTINGS_TABS = new Set(['quick-punch']);

export const ProjectSettingsPage = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const numericProjectId = projectId ? Number(projectId) : undefined;
  const { toast } = useToast();
  const summary = useProjectSummary(numericProjectId);
  const configuration = useQuickPunchProjectConfiguration(numericProjectId);
  const updateConfiguration = useUpdateQuickPunchProjectConfiguration(numericProjectId);

  if (!numericProjectId || configuration.isLoading) {
    return (
      <BaseLayout>
        <div className="grid min-h-[60vh] place-items-center text-muted-foreground">
          <div className="text-center">
            <Loader2 className="mx-auto mb-3 size-6 animate-spin text-primary" />
            Loading project settings…
          </div>
        </div>
      </BaseLayout>
    );
  }

  if (configuration.isError || !configuration.data) {
    return (
      <BaseLayout>
        <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 lg:px-9">
          <Card className="border-destructive/30">
            <CardContent className="p-6">
              <p className="text-sm text-destructive">
                {configuration.error
                  ? getErrorMessage(configuration.error)
                  : 'Project settings are unavailable.'}
              </p>
            </CardContent>
          </Card>
        </div>
      </BaseLayout>
    );
  }

  const data = configuration.data;
  const projectName = summary.data?.project.name ?? 'Project';
  const tabParam = searchParams.get('tab') ?? 'quick-punch';
  const activeTab = PROJECT_SETTINGS_TABS.has(tabParam) ? tabParam : 'quick-punch';

  const handleTabChange = (value: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (value === 'quick-punch') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', value);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const quickPunchSettings = (
    <Card className="overflow-hidden">
      <CardHeader className="border-b border-border px-5 py-5 sm:px-6">
        <CardTitle className="font-display text-xl font-bold tracking-[-0.02em]">
          <h2>Quick Punch</h2>
        </CardTitle>
        <p className="text-sm leading-6 text-muted-foreground">
          Set the fields and default values required whenever someone creates a Quick Punch item.
        </p>
      </CardHeader>
      <CardContent className="p-5 sm:p-6">
        <QuickPunchConfigurationEditor
          key={data.project_configuration.updated_at ?? 'new-policy'}
          projectId={numericProjectId}
          fields={data.available_fields}
          mode="project"
          selectedFields={data.project_configuration.required_fields}
          requiredFields={data.project_configuration.required_fields}
          defaults={data.project_configuration.defaults}
          submitLabel="Save Quick Punch settings"
          isSaving={updateConfiguration.isPending}
          onSave={async ({ requiredFields, defaults }) => {
            try {
              await updateConfiguration.mutateAsync({
                required_fields: requiredFields.filter((field) => field !== 'description'),
                defaults,
              });
              toast({
                title: 'Quick Punch settings saved',
                description: 'These requirements now apply to every user on this project.',
              });
            } catch (caught) {
              toast({
                title: 'Unable to save Quick Punch settings',
                description: getErrorMessage(caught),
                variant: 'destructive',
              });
            }
          }}
        />
      </CardContent>
    </Card>
  );

  return (
    <BaseLayout>
      <div className="min-h-screen bg-background pb-12 pt-24">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold tracking-[-0.02em] text-foreground">
              Project Settings
            </h1>
            <p className="mt-2 text-muted-foreground">
              Manage project-wide configuration for {projectName}.
            </p>
          </div>

          <VerticalTab
            value={activeTab}
            onValueChange={handleTabChange}
            sidebarPanelClassName="rounded-[1.375rem] p-2.5 shadow-slate"
            items={[
              {
                value: 'quick-punch',
                label: (
                  <span className="flex items-center gap-3">
                    <Zap className="size-4 shrink-0" strokeWidth={1.9} />
                    <span>Quick Punch</span>
                  </span>
                ),
                content: quickPunchSettings,
              },
            ]}
          />
        </div>
      </div>
    </BaseLayout>
  );
};
